import { useState, useEffect, useCallback, useRef } from "react";

type UsePollingOptions = {
  interval: number; // Interval in milliseconds
  startImmediate?: boolean; // Start polling immediately
  refetchOnPushState?: boolean; // Refetch when comeback to origin
};

type UsePollingReturn = {
  start: () => void;
  stop: () => void;
  isRunning: boolean;
  refetchOnPushState: boolean;
};

export const usePollingSimple = (
  callback: () => void,
  interval: number,
  startImmediate = true
) => {
  const [isRunning, setIsRunning] = useState<boolean>(startImmediate);
  const savedCallback = useRef(callback);

  // Keep callback up to date
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval
  useEffect(() => {
    if (!isRunning) return;

    const id = setInterval(() => {
      savedCallback.current();
    }, interval);

    return () => clearInterval(id);
  }, [isRunning, interval]);

  // Auto start/stop logic
  useEffect(() => {
    if (startImmediate) {
      setIsRunning(true);
    }
    return () => {
      setIsRunning(false);
    };
  }, [startImmediate]);

  const start = useCallback(() => {
    setIsRunning(true);
  }, []);

  const stop = useCallback(() => {
    setIsRunning(false);
  }, []);

  return { start, stop, isRunning };
};

export const usePolling = (
  callback: () => void,
  originalUrl: string,
  { interval, startImmediate = true, refetchOnPushState = false }: UsePollingOptions
): UsePollingReturn => {
  const [isRunning, setIsRunning] = useState<boolean>(startImmediate);
  const savedCallback = useRef<() => void>();
  const _originalUrl = useRef<string>(originalUrl);

  // Remember the latest callback.
  useEffect(() => {
    if (
      window.location.pathname === _originalUrl.current ||
      _originalUrl.current === "*" ||
      matchAndExtract(_originalUrl.current, window.location.pathname)
    ) {
      savedCallback.current = callback;
    } else {
      // check if originalUrl is different from the current pathname
      savedCallback.current = () => void 0;
    }
  }, [callback]);

  const start = useCallback(() => {
    setIsRunning(true);
  }, []);

  const stop = useCallback(() => {
    setIsRunning(false);
  }, []);

  useEffect(() => {
    if (!isRunning) return;

    if (startImmediate) {
      if (savedCallback.current) {
        savedCallback.current();
      }
    }

    const tick = () => {
      if (savedCallback.current) {
        savedCallback.current();
      }
    };

    const id = setInterval(tick, interval);
    return () => clearInterval(id);
  }, [isRunning, interval]);

  useEffect(() => {
    if (startImmediate) {
      start();
    }
    return () => {
      stop();
    };
  }, [startImmediate, start, stop]);

  // Pause polling when the window is not active or when navigating away
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        stop();
      } else if (startImmediate) {
        start();
      }
    };

    const handleNavigation = () => {
      if (
        window.location.pathname === _originalUrl.current ||
        matchAndExtract(_originalUrl.current, window.location.pathname)
      ) {
        if (startImmediate) {
          start();
        }

        if (refetchOnPushState) {
          callback();
        }
      } else {
        stop();
      }
    };

    const originalPushState = window.history.pushState;
    window.history.pushState = function (...args) {
      originalPushState.apply(window.history, args);
      window.dispatchEvent(new Event("pushstate"));
    };

    window.addEventListener("popstate", handleNavigation);
    window.addEventListener("pushstate", handleNavigation);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.history.pushState = originalPushState;
      window.removeEventListener("popstate", handleNavigation);
      window.removeEventListener("pushstate", handleNavigation);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [start, stop, startImmediate]);

  useEffect(() => {
    const handleForcePoll = () => {
      console.log("Force poll triggered");
      if (savedCallback.current) {
        savedCallback.current();
      }
    };

    window.addEventListener("forcepoll", handleForcePoll);
    return () => {
      window.removeEventListener("forcepoll", handleForcePoll);
    };
  }, []);

  return { start, stop, isRunning, refetchOnPushState };
};

function matchAndExtract(pattern, url) {
  if (pattern === "*") {
    return false;
  }

  const paramNames = [];
  const regex = new RegExp(
    "^" +
      pattern.replace(/:([^/]+)/g, (_, name) => {
        paramNames.push(name);
        return "([^/]+)";
      }) +
      "$"
  );

  const match = url.match(regex);
  if (!match) return null;

  return paramNames.reduce((acc, name, i) => {
    acc[name] = match[i + 1];
    return acc;
  }, {});
}
